/**
 * Docking runtime (M2 spec §7.2, plan §2.3, §2.7, M2-14): clearance, the auto-dock autopilot through the real
 * flight model, dock detection, launch and station impacts, for the player's ship.
 *
 * - **request(station)**: clearance for the station's bay. Refused (`clearanceDenied`, `CLEARANCE_REASON`) when
 *   the station's hull is beyond `requestRange` (`tooFar`; every range here is from the hull, `|p − centre| − radius`,
 *   scale build §3.3), its model is not attached yet (`notReady`), the bay doesn't
 *   take the player def's size band (`tooLarge`, M10 P1e1: `bayAccepts(bay size, def.sizeBand)`, only while
 *   `moor.json enabled` is off) or a hostile ship holds a torpedo lock on the player (`hostileLock`); else
 *   `clearanceGranted` (f = seconds) and the phase becomes `cleared`. A new grant replaces an older one; a repeat
 *   renews it.
 * - **Moor** (M10 P1e2, `space/moorFlow.ts`): with `moor.json enabled` on, a request from a hull the bay doesn't
 *   take is a moor request: refused `tooFar` beyond `moor.rangeM` of the hull, `notReady` or `hostileLock` as above, else
 *   granted (`clearanceGranted` with c = 1, then `moorGranted`) and the moor autopilot flies the ship to its ring
 *   point at once (phase `autodock`, station -1; flight assist forced on and restored after). Flight input cancels it
 *   (`autodockCancel`). Held inside `holdToleranceM` under `holdSpeedMps` for `dock.holdSeconds`, it completes
 *   exactly like a bay dock (the clamps, phase `docked`, `docked`) and then emits `moored`: moored = docked.
 * - **startAutodock()**: needs a live grant and the station's hull within `autodockRange`; forces flight assist on (the
 *   autopilot's stick maths needs it) and restores the pilot's setting when it stops. Emits `autodockStart`.
 * - **control stage**: records the pre-step velocity and rotation (for impacts); while docked it holds every
 *   `Control` axis at zero; while auto-docking it runs `stepAutodock`, which writes the player's `Control` exactly as
 *   an AI pilot would (D-013), and any flight input (`input.flightActive`) cancels it (`autodockCancel`).
 * - **physics stage**: fills the readout against the cleared station's bay (`corridorState`), steps the dock hold
 *   (`stepDockHold`) and completes the dock: the clamps zero `Control`, `Thrust` and the velocity, the clearance is
 *   used up, the phase is `docked` and `docked` is emitted. Then **impacts**: for the player within
 *   `impact.proximity` of a ready station's bounds, the velocity change this tick's thrust cannot explain (the exact
 *   form of `impactSpeed`: the pre-step velocity plus this tick's thrust acceleration, rebuilt from `Thrust`) is a
 *   collision; its `impactDamage` goes straight to `Hull` (collisions skip shields) with an M1 `hitHull`
 *   (eid player, other −1) and an M2 `impact` event.
 * - **end stage**: expires the clearance (`clearanceExpired`, which also stops an auto-dock), and drops the
 *   clearance and the auto-dock of a dead player.
 * - **dockAt(station)** puts the player on the pad facing inward at rest, phase `docked`, no event (career start,
 *   load, respawn). **launch(station)** puts it at the bay mouth facing out at `launch.speed` along the out axis,
 *   phase `space`, and emits `undocked`. Both work before the station model is attached (the bay frame then comes
 *   from the asset manifest; `launch` normally waits for the models anyway).
 *   **A hull the bay doesn't take** (M10 P1e1, an L) is never put in the bay: `dockAt` places it at rest at its
 *   mooring-ring point (`docking/moor.ts moorRingPoint`, slot `moor.json playerSlot`) with its nose along the ring
 *   tangent, and `launch` releases it there facing radially out at `moor.json launchSpeedMps`.
 *
 * Times are game time (`mirror.gameTime`). Stage functions are allocation-free (D-014); positions are float64
 * world metres (D-011).
 */
import { SIM_DT } from '../core/loop'
import { QuatScratch, Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import { emitEvent, EV } from '../combat/events'
import { isHostile } from '../combat/hostility'
import { ControlScratch, createAutodockState, noseUpInto, originForHullCentre, originForHullCentreFrom, resetAutodock, stepAutodockAt, type AutodockShip, type AutodockState } from '../docking/autopilot'
import { cradleConfig, cradleFrame, type CradleConfig } from '../docking/cradle'
import { bayFrameFromSocket, createBayFrame, Mat3Scratch, quatFromMatrix, quatFromMatrixOf } from '../docking/bay'
import { bayAccepts } from '../data/bands'
import type { ShipBand } from '../data/schemas'
import { moorRingPoint, moorRingTangent, moorRingUp, moorSlotDirection } from '../docking/moor'
import { moorConfig, type MoorConfig } from '../docking/moorConfig'
import {
  CLEARANCE_STATUS,
  clearanceLeft,
  createClearanceState,
  releaseClearance,
  requestClearance,
  stepClearance,
  type ClearanceReason,
  type ClearanceState,
} from '../docking/clearance'
import { corridorState } from '../docking/corridor'
import { stepDockHold } from '../docking/detect'
import { ImpactFloats, ThrustSample, impactDamageTo, impactSpeedTo, thrustAccelerationFrom } from '../docking/impact'
import type { ControlInput } from '../flight/flightModel'
import { entityExists } from 'bitecs'
import { attachShip, detachShip, pinCarriedPoseFrom, setShipPose, type Sim } from '../sim'
import { AngVel, Control, Hull, Life, Lock, Pos64, Rot, Ship, Team, Thrust, Vel } from '../world/components'
import {
  createDockingReadout,
  type BayFrame,
  type CradleCarrierLink,
  type CradleStatus,
  type DockingReadout,
  type DockingRuntime,
  type SpaceContext,
  type StationsRuntime,
} from './contracts'
import { ToolControl } from './components'
import { CLEARANCE_REASON, emitSpaceEvent, SEV } from './events'
import { cancelCradle, CRADLE_PHASE, cradlePadVelocity, createCradleFlow, requestCradle, stepCradleFlow, type CradleCarrier, type CradleFlowState, type CradleTuning } from './cradleFlow'
import { clearMoor, createMoorFlowState, moorFlying, setMoored, startMoor, stepMoorFlight, stepMoorHold, type MoorFlowState } from './moorFlow'
import { findBaySocket, stationBaySize, stationModelEntry } from './stations'


/** Everything the docking stage functions share (one per space world). */
export interface DockingState {
  readonly ctx: SpaceContext
  readonly stations: StationsRuntime
  /** Mooring tuning (M10 P1e1): the ring point a hull too big for the bay loads and launches at. */
  readonly moor: MoorConfig
  readonly readout: DockingReadout
  readonly clearance: ClearanceState
  readonly autodock: AutodockState
  /** The autopilot is flying the player. */
  autodocking: boolean
  /** Station of the running auto-dock (-1 when none). */
  autodockStation: number
  /** The pilot's `Ship.faOn` before auto-dock forced it on. */
  faBefore: number
  /** The player is clamped in a bay. */
  docked: boolean
  dockedStation: number
  /** The control stage recorded this tick's pre-step velocity and rotation. */
  preValid: boolean
  readonly preVel: Vec3
  readonly preRot: Quat
  // Scratch (D-014).
  readonly ship: AutodockShip & { pos: Vec3; vel: Vec3; rot: Quat }
  readonly control: ControlInput
  readonly fwd: Vec3
  readonly up: Vec3
  readonly accel: Vec3
  readonly expected: Vec3
  readonly post: Vec3
  readonly quat: Quat
  readonly frame: BayFrame
  /** The player moor flow (M10 P1e2): kept here, never on `DockingReadout`/`DockingRuntime` (m8e contracts). */
  readonly moorFlow: MoorFlowState
  /** Ring scratch (M10 P1e1): the slot point, the nose and the up. */
  readonly ringPoint: Vec3
  readonly ringNose: Vec3
  readonly ringUp: Vec3
  /**
   * The cradle (M10 P5a9b2): the own L the dock key may ask (null = none: today's game, nothing below runs), the
   * flow, whether the S rides in the cradle (detached, pinned to the pad each tick), and the pilot's FA setting.
   */
  carrier: CradleCarrierLink | null
  readonly cradleCfg: CradleConfig
  readonly cradleTuning: CradleTuning
  readonly cradle: CradleFlowState
  carried: boolean
  cradleFaBefore: number
  /**
   * M10 P5a10a (review C3, C6, C7): the carrier's def index at registration (a recycled eid with another def is not
   * the carrier), whether a request is waiting for the L to settle into its hold (Q9: the request orders the hold),
   * and the L's own flight assist before the hold order forced it on (-1 = no hold applied yet).
   */
  carrierDef: number
  cradleWaiting: boolean
  carrierFaBefore: number
  /** Scratch: the carrier's pose and motion this tick, and the pad velocity (D-014). */
  readonly carrierView: CradleCarrier & { pos: Vec3; rot: Quat; vel: Vec3; angVel: Vec3; radius: number }
  readonly padVel: Vec3
  readonly cradleStatus: CradleStatus
}

export function createDockingState(ctx: SpaceContext, stations: StationsRuntime, moor: MoorConfig = moorConfig()): DockingState {
  return {
    ctx,
    stations,
    moor,
    readout: createDockingReadout(),
    clearance: createClearanceState(),
    autodock: createAutodockState(),
    autodocking: false,
    autodockStation: -1,
    faBefore: 1,
    docked: false,
    dockedStation: -1,
    preValid: false,
    preVel: new Vec3Scratch(),
    preRot: new QuatScratch(),
    ship: { pos: new Vec3Scratch(), vel: new Vec3Scratch(), rot: new QuatScratch() },
    control: new ControlScratch(),
    fwd: new Vec3Scratch(0, 0, -1),
    up: new Vec3Scratch(0, 1, 0),
    accel: new Vec3Scratch(),
    expected: new Vec3Scratch(),
    post: new Vec3Scratch(),
    quat: new QuatScratch(),
    frame: createBayFrame(),
    moorFlow: createMoorFlowState(),
    ringPoint: new Vec3Scratch(),
    ringNose: new Vec3Scratch(0, 0, -1),
    ringUp: new Vec3Scratch(0, 1, 0),
    carrier: null,
    cradleCfg: cradleConfig(),
    cradleTuning: { moor, cradle: cradleConfig() },
    cradle: createCradleFlow(),
    carried: false,
    cradleFaBefore: 1,
    carrierDef: -1,
    cradleWaiting: false,
    carrierFaBefore: -1,
    carrierView: { pos: new Vec3Scratch(), rot: new QuatScratch(), vel: new Vec3Scratch(), angVel: new Vec3Scratch(), radius: 0 },
    padVel: new Vec3Scratch(),
    cradleStatus: { phase: 'none', holdOrdered: false, carried: false, carrier: -1 },
  }
}

export function createDockingRuntime(ctx: SpaceContext, stations: StationsRuntime, moor: MoorConfig = moorConfig()): DockingRuntime {
  const st = createDockingState(ctx, stations, moor)
  return {
    readout: st.readout,
    request: (station: number): boolean => requestDocking(st, station),
    requestRange: (station: number): number => requestRangeAt(st, station),
    startAutodock: (): boolean => startAutodock(st),
    cancelAutodock: (): void => cancelAutodock(st),
    dockAt: (station: number): void => dockAt(st, station),
    launch: (station: number): void => launch(st, station),
    cradleKey: (): boolean => cradleKey(st),
    setCradleCarrier: (link: CradleCarrierLink | null): void => setCradleCarrier(st, link),
    launchCradle: (): boolean => launchCradle(st),
    cradleStatus: (): CradleStatus => cradleStatusOf(st),
    control: (sim: Sim): void => dockingControl(st, sim),
    physics: (sim: Sim): void => dockingPhysics(st, sim),
    end: (sim: Sim): void => dockingEnd(st, sim),
  }
}

// ---------------------------------------------------------------------------------------------
// Helpers (allocation-free)
// ---------------------------------------------------------------------------------------------

/** The live player's eid, or -1. */
function livePlayer(sim: Sim): number {
  const p = sim.playerEid
  return p >= 0 && Life.alive[p] === 1 ? p : -1
}

/** Phase, station and clearance time of the readout from the runtime state. */
export function syncPhase(st: DockingState): void {
  const r = st.readout
  if (st.docked) {
    r.phase = 'docked'
    r.station = st.dockedStation
  } else if (st.autodocking) {
    r.phase = 'autodock'
    r.station = st.autodockStation
  } else if (st.carried || st.cradleWaiting || st.cradle.phase === CRADLE_PHASE.approach) {
    // The cradle autopilot flies the ship (M10 P5a9b2), or it rides in the cradle or waits for the L to hold
    // (P5a10a, review C1): never `space`, so clearance and the phase-gated systems stay off. No station.
    r.phase = 'autodock'
    r.station = -1
  } else if (moorFlying(st.moorFlow)) {
    // The moor autopilot flies the ship (M10 P1e2). No station: the bay guides don't apply to a ring approach.
    r.phase = 'autodock'
    r.station = -1
  } else if (st.clearance.status === CLEARANCE_STATUS.granted) {
    r.phase = 'cleared'
    r.station = st.clearance.station
  } else {
    r.phase = 'space'
    r.station = -1
  }
  r.clearanceLeft = st.docked ? 0 : clearanceLeft(st.clearance, st.ctx.mirror.gameTime)
}

/** Zeroes the geometric readout fields (no bay to measure against). */
function clearGuides(r: DockingReadout): void {
  r.inCorridor = false
  r.inBay = false
  r.along = 0
  r.lateral = 0
  r.speedLimit = 0
  r.alignDeg = 0
  r.rollDeg = 0
  r.padDistance = 0
  r.holdProgress = 0
}

/** True when a live ship of a hostile team (`isHostile`) holds a torpedo lock on `p`. */
export function hostileLockOn(sim: Sim, p: number): boolean {
  const ships = sim.ships.dense
  for (let i = 0; i < ships.length; i++) {
    const e = ships[i]
    if (e !== p && Life.alive[e] === 1 && isHostile(Team.id[e], Team.id[p]) && Lock.target[e] === p && Lock.locked[e] === 1) return true
  }
  return false
}

function zeroShipControl(p: number): void {
  Control.surge[p] = 0
  Control.sway[p] = 0
  Control.heave[p] = 0
  Control.pitch[p] = 0
  Control.yaw[p] = 0
  Control.roll[p] = 0
  Control.boost[p] = 0
  Control.firePrimary[p] = 0
  Control.fireSecondary[p] = 0
}

function zeroThrust(p: number): void {
  Thrust.surge[p] = 0
  Thrust.sway[p] = 0
  Thrust.heave[p] = 0
  Thrust.pitch[p] = 0
  Thrust.yaw[p] = 0
  Thrust.roll[p] = 0
}

function readShip(st: DockingState, p: number): void {
  const s = st.ship
  s.pos.x = Pos64.x[p]
  s.pos.y = Pos64.y[p]
  s.pos.z = Pos64.z[p]
  s.vel.x = Vel.x[p]
  s.vel.y = Vel.y[p]
  s.vel.z = Vel.z[p]
  s.rot.x = Rot.x[p]
  s.rot.y = Rot.y[p]
  s.rot.z = Rot.z[p]
  s.rot.w = Rot.w[p]
  // Objects only across the call (P5a11b: the four quaternion floats boxed per un-inlined rotation, D-1052), and the
  // axes written out, not `rotateVecFrom` with the pooled axes (4 boxes per tick on the station rig; DA-1, D-TBD).
  noseUpInto(s.rot, st.fwd, st.up)
}

/**
 * The ship rotation that points its nose (local −Z) along `sign × −out` of the bay with its up on the bay's up:
 * `sign = 1` faces into the bay (docked), `sign = −1` faces out of the mouth (launch). Into `out`.
 */
export function bayFacing(frame: Readonly<BayFrame>, sign: 1 | -1, out: Quat): Quat {
  // Columns = world images of the ship's local axes: X = −sign × right, Y = up, Z = sign × out (nose = −Z).
  const r = frame.right
  const u = frame.up
  const o = frame.out
  // Through a matrix scratch, not nine float arguments (P5a11b: the per-tick cradle pin, D-1052).
  const m = facingM
  m.m00 = -sign * r.x
  m.m01 = u.x
  m.m02 = sign * o.x
  m.m10 = -sign * r.y
  m.m11 = u.y
  m.m12 = sign * o.y
  m.m20 = -sign * r.z
  m.m21 = u.z
  m.m22 = sign * o.z
  return quatFromMatrixOf(m, out)
}
const facingM = new Mat3Scratch()

/** The station's bay frame: the attached one, else built from the asset manifest into scratch. Throws when neither exists. */
function stationBay(st: DockingState, station: number): BayFrame {
  const s = st.stations.list[station]
  if (s.bay) return s.bay
  const entry = stationModelEntry(st.ctx, s.def)
  const socket = entry ? findBaySocket(entry, s.def.baySocket) : null
  if (!socket) throw new Error(`station ${s.def.id}: no ${s.def.baySocket} socket to place the ship at`)
  return bayFrameFromSocket(s.pos, s.rot, socket, st.frame)
}

/** The player def's size band (M10 P1d `ShipDef.sizeBand`). */
/** Scratch for the docked and launch poses' ship origin (D-014). */
const poseOrigin: Vec3 = new Vec3Scratch()

function playerBand(sim: Sim, p: number): ShipBand {
  return sim.defs[Ship.defIndex[p]].sizeBand
}

/** Whether station `station`'s bay takes a ship of `band` (`bayAccepts`; a bay socket without a size is `moor.defaultBaySize`). */
export function bayTakes(st: DockingState, station: number, band: ShipBand): boolean {
  return bayAccepts(stationBaySize(st.stations.list[station]) ?? st.moor.defaultBaySize, band)
}

/**
 * The player's mooring-ring pose at `station` (slot `moor.playerSlot`, out axis from `frame`): the point into
 * `st.ringPoint`, the up into `st.ringUp`, and the rotation into `st.quat` with the nose along the ring tangent
 * (`outward` false, docked) or radially out (`outward` true, launch; the direction is also left in `st.ringNose`).
 */
function ringPose(st: DockingState, station: number, frame: Readonly<BayFrame>, outward: boolean): void {
  const s = st.stations.list[station]
  const cfg = st.moor
  moorRingPoint(s.pos, s.radius, frame.out, cfg.playerSlot, cfg, st.ringPoint)
  moorRingUp(frame.out, st.ringUp)
  const n = st.ringNose
  if (outward) moorSlotDirection(frame.out, cfg.playerSlot, cfg, n)
  else moorRingTangent(frame.out, cfg.playerSlot, cfg, n)
  const u = st.ringUp
  // Columns = world images of the ship's local axes: X = nose × up, Y = up, Z = −nose (nose = −Z).
  quatFromMatrix(n.y * u.z - n.z * u.y, u.x, -n.x, n.z * u.x - n.x * u.z, u.y, -n.y, n.x * u.y - n.y * u.x, u.z, -n.z, st.quat)
}

/** Stops the autopilot and gives the pilot back their flight-assist setting. `emit` sends `autodockCancel`. */
function stopAutodock(st: DockingState, emit: boolean): void {
  if (!st.autodocking) return
  const sim = st.ctx.sim
  const p = sim.playerEid
  if (p >= 0) Ship.faOn[p] = st.faBefore
  st.autodocking = false
  const station = st.autodockStation
  st.autodockStation = -1
  if (emit) emitSpaceEvent(st.ctx.events, SEV.autodockCancel, p, station, 0, 0, 0, 0, 0, 0, 0)
}

/** Stops the moor flight (M10 P1e2) and gives the pilot back their flight-assist setting. `emit` sends `autodockCancel`. */
function stopMoor(st: DockingState, emit: boolean): void {
  const flow = st.moorFlow
  if (!moorFlying(flow)) return
  const p = st.ctx.sim.playerEid
  if (p >= 0) Ship.faOn[p] = flow.faBefore
  const station = flow.station
  clearMoor(flow)
  if (emit) emitSpaceEvent(st.ctx.events, SEV.autodockCancel, p, station, 0, 0, 0, 0, 0, 0, 0)
}

/**
 * Grants player `p` a moor at `station` (M10 P1e2): the moor flight starts at once toward ring slot
 * `moor.playerSlot`, flight assist forced on. Emits `clearanceGranted` (c = 1, no window) and `moorGranted`.
 */
function grantMoor(st: DockingState, p: number, station: number, out: Readonly<Vec3>): boolean {
  const flow = st.moorFlow
  const s = st.stations.list[station]
  if (st.autodocking) stopAutodock(st, true)
  if (moorFlying(flow) && flow.station === station) return true
  const faOn = moorFlying(flow) ? flow.faBefore : Ship.faOn[p]
  const slot = st.moor.playerSlot
  startMoor(flow, station, slot, faOn)
  moorRingPoint(s.pos, s.radius, out, slot, st.moor, flow.target)
  Ship.faOn[p] = 1
  emitSpaceEvent(st.ctx.events, SEV.clearanceGranted, p, station, 0, 1, 0, 0, 0, 0, 0)
  emitSpaceEvent(st.ctx.events, SEV.moorGranted, p, station, slot, 0, 0, 0, 0, 0, 0)
  syncPhase(st)
  return true
}

// ---------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------

/**
 * `DockingRuntime.requestRange`: how far from station `station`'s hull a request can be granted for the player's hull,
 * the same range `requestDocking` checks: `moor.rangeM` for a hull that moors there (moor on and the bay doesn't take
 * it), else `docking.requestRange`. No live player or no such station reads `docking.requestRange`.
 */
export function requestRangeAt(st: DockingState, station: number): number {
  const p = livePlayer(st.ctx.sim)
  if (p >= 0 && st.moor.enabled && station >= 0 && station < st.stations.list.length && !bayTakes(st, station, playerBand(st.ctx.sim, p))) {
    return st.moor.rangeM
  }
  return st.ctx.data.docking.requestRange
}

/** `DockingRuntime.request`. */
export function requestDocking(st: DockingState, station: number): boolean {
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  const s = st.stations.list[station] as (typeof st.stations.list)[number] | undefined
  if (p < 0 || st.docked || !s) return false
  const cfg = st.ctx.data.docking
  // Ranges are from the hull (scale build §3.3): the distance to the centre less the station's bounding radius.
  const distance = Math.hypot(Pos64.x[p] - s.pos.x, Pos64.y[p] - s.pos.y, Pos64.z[p] - s.pos.z) - s.radius
  let reason: ClearanceReason
  if (st.moor.enabled && !bayTakes(st, station, playerBand(sim, p))) {
    // A hull the bay doesn't take moors at the ring (M10 P1e2), within moor.rangeM of the station's hull (requestRangeAt).
    if (!(distance <= requestRangeAt(st, station))) reason = CLEARANCE_REASON.tooFar
    else if (!s.ready || !s.bay) reason = CLEARANCE_REASON.notReady
    else if (hostileLockOn(sim, p)) reason = CLEARANCE_REASON.hostileLock
    else return grantMoor(st, p, station, s.bay.out)
    emitSpaceEvent(st.ctx.events, SEV.clearanceDenied, p, station, reason, 0, 0, 0, 0, 0, 0)
    return false
  }
  if (!(distance <= cfg.requestRange)) reason = CLEARANCE_REASON.tooFar
  else if (!s.ready || !s.bay) reason = CLEARANCE_REASON.notReady
  // With moor.json enabled off, a hull the bay doesn't take is refused here (P1e1).
  else if (!bayTakes(st, station, playerBand(sim, p))) reason = CLEARANCE_REASON.tooLarge
  else if (hostileLockOn(sim, p)) reason = CLEARANCE_REASON.hostileLock
  else reason = requestClearance(st.clearance, station, distance, st.ctx.mirror.gameTime, cfg)
  if (reason !== CLEARANCE_REASON.none) {
    emitSpaceEvent(st.ctx.events, SEV.clearanceDenied, p, station, reason, 0, 0, 0, 0, 0, 0)
    return false
  }
  if (st.autodocking && st.autodockStation !== station) stopAutodock(st, true)
  emitSpaceEvent(st.ctx.events, SEV.clearanceGranted, p, station, 0, 0, 0, 0, 0, cfg.clearanceSeconds, 0)
  syncPhase(st)
  return true
}

/** `DockingRuntime.startAutodock`. */
export function startAutodock(st: DockingState): boolean {
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  if (p < 0 || st.docked || st.clearance.status !== CLEARANCE_STATUS.granted) return false
  if (st.autodocking) return true
  const station = st.clearance.station
  const s = st.stations.list[station]
  if (!s.ready || !s.bay) return false
  const distance = Math.hypot(Pos64.x[p] - s.pos.x, Pos64.y[p] - s.pos.y, Pos64.z[p] - s.pos.z) - s.radius
  if (!(distance <= st.ctx.data.docking.autodockRange)) return false
  st.faBefore = Ship.faOn[p]
  Ship.faOn[p] = 1
  resetAutodock(st.autodock)
  st.autodocking = true
  st.autodockStation = station
  emitSpaceEvent(st.ctx.events, SEV.autodockStart, p, station, 0, 0, 0, 0, 0, 0, 0)
  syncPhase(st)
  return true
}

/** `DockingRuntime.cancelAutodock`. */
export function cancelAutodock(st: DockingState): void {
  if (st.autodocking) stopAutodock(st, true)
  else if (moorFlying(st.moorFlow)) stopMoor(st, true)
  else if (st.cradle.phase === CRADLE_PHASE.approach) stopCradle(st)
  else return
  syncPhase(st)
}

/** `DockingRuntime.dockAt`. */
export function dockAt(st: DockingState, station: number): void {
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  if (p < 0 || !st.stations.list[station]) return
  const frame = stationBay(st, station)
  stopAutodock(st, false)
  stopMoor(st, false)
  releaseClearance(st.clearance)
  if (bayTakes(st, station, playerBand(sim, p))) {
    clearMoor(st.moorFlow)
    bayFacing(frame, 1, st.quat)
    // The hull box centred on the pad, as the autopilot flies it (D-TBD).
    const o = originForHullCentre(frame.pad.x, frame.pad.y, frame.pad.z, st.quat, sim.defs[Ship.defIndex[p]].hullCentre, poseOrigin)
    setShipPose(sim, p, o.x, o.y, o.z, st.quat, 0, 0, 0)
  } else {
    setMoored(st.moorFlow, station, st.moor.playerSlot)
    ringPose(st, station, frame, false)
    const r = st.ringPoint
    setShipPose(sim, p, r.x, r.y, r.z, st.quat, 0, 0, 0)
  }
  zeroShipControl(p)
  zeroThrust(p)
  st.docked = true
  st.dockedStation = station
  st.preValid = false
  markDocked(st.readout)
  syncPhase(st)
}

/** `DockingRuntime.launch`. */
export function launch(st: DockingState, station: number): void {
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  if (p < 0 || !st.stations.list[station]) return
  const frame = stationBay(st, station)
  const inBay = bayTakes(st, station, playerBand(sim, p))
  const speed = inBay ? st.ctx.data.docking.launch.speed : st.moor.launchSpeedMps
  stopAutodock(st, false)
  stopMoor(st, false)
  clearMoor(st.moorFlow)
  releaseClearance(st.clearance)
  let m: Vec3
  if (inBay) {
    bayFacing(frame, -1, st.quat)
    // The hull box centred on the mouth (D-TBD).
    m = originForHullCentre(frame.mouth.x, frame.mouth.y, frame.mouth.z, st.quat, sim.defs[Ship.defIndex[p]].hullCentre, poseOrigin)
    setShipPose(sim, p, m.x, m.y, m.z, st.quat, frame.out.x * speed, frame.out.y * speed, frame.out.z * speed)
  } else {
    ringPose(st, station, frame, true)
    m = st.ringPoint
    const n = st.ringNose
    setShipPose(sim, p, m.x, m.y, m.z, st.quat, n.x * speed, n.y * speed, n.z * speed)
  }
  zeroThrust(p)
  st.docked = false
  st.dockedStation = -1
  st.preValid = false
  clearGuides(st.readout)
  st.readout.speed = speed
  syncPhase(st)
  emitSpaceEvent(st.ctx.events, SEV.undocked, p, station, 0, 0, m.x, m.y, m.z, 0, 0)
}

function markDocked(r: DockingReadout): void {
  clearGuides(r)
  r.inBay = true
  r.speed = 0
  r.holdProgress = 1
}


// ---------------------------------------------------------------------------------------------
// Stages (allocation-free, D-014)
// ---------------------------------------------------------------------------------------------

/** Control stage: pre-step snapshot, the docked hold and the autopilot. */
export function dockingControl(st: DockingState, sim: Sim): void {
  const p = livePlayer(sim)
  if (p < 0) {
    st.preValid = false
    return
  }
  st.preVel.x = Vel.x[p]
  st.preVel.y = Vel.y[p]
  st.preVel.z = Vel.z[p]
  st.preRot.x = Rot.x[p]
  st.preRot.y = Rot.y[p]
  st.preRot.z = Rot.z[p]
  st.preRot.w = Rot.w[p]
  st.preValid = true
  if (st.carrier !== null && cradleControl(st, sim, p)) return
  if (st.docked) {
    zeroShipControl(p)
    return
  }
  if (moorFlying(st.moorFlow)) {
    mooringControl(st, sim, p)
    return
  }
  if (!st.autodocking) return
  if (st.ctx.input.flightActive) {
    cancelAutodock(st)
    return
  }
  const s = st.stations.list[st.autodockStation]
  const frame = s.bay
  if (!frame) {
    cancelAutodock(st)
    return
  }
  Ship.faOn[p] = 1
  readShip(st, p)
  const def = sim.defs[Ship.defIndex[p]]
  // The station object, not its radius: a float into the un-inlined step is boxed (DA-1, D-TBD).
  const result = stepAutodockAt(st.autodock, frame, s, st.ship, def, st.ctx.data.docking, SIM_DT, st.control)
  if (result === 'failed') {
    cancelAutodock(st)
    return
  }
  const c = st.control
  Control.surge[p] = c.surge
  Control.sway[p] = c.sway
  Control.heave[p] = c.heave
  Control.pitch[p] = c.pitch
  Control.yaw[p] = c.yaw
  Control.roll[p] = c.roll
  Control.boost[p] = 0
}

/** Physics stage: the readout, the dock hold and station impacts. */
export function dockingPhysics(st: DockingState, sim: Sim): void {
  const p = livePlayer(sim)
  const r = st.readout
  if (p < 0) {
    clearGuides(r)
    r.speed = 0
    return
  }
  if (st.docked) {
    markDocked(r)
    return
  }
  if (st.carried) {
    pinCarried(st, sim, p)
    clearGuides(r)
    r.speed = 0
    return
  }
  readShip(st, p)
  applyStationImpacts(st, sim, p)
  if (moorFlying(st.moorFlow)) {
    clearGuides(r)
    r.speed = Math.sqrt(st.ship.vel.x * st.ship.vel.x + st.ship.vel.y * st.ship.vel.y + st.ship.vel.z * st.ship.vel.z)
    if (stepMoorHold(st.moorFlow, st.ship.pos, st.ship.vel, st.moor, st.ctx.data.docking.dock.holdSeconds, SIM_DT)) completeMoor(st, sim, p)
    return
  }
  const target = st.autodocking ? st.autodockStation : st.clearance.status === CLEARANCE_STATUS.granted ? st.clearance.station : -1
  const frame = target >= 0 ? st.stations.list[target].bay : null
  if (!frame) {
    clearGuides(r)
    r.speed = Math.sqrt(st.ship.vel.x * st.ship.vel.x + st.ship.vel.y * st.ship.vel.y + st.ship.vel.z * st.ship.vel.z)
    return
  }
  const cfg = st.ctx.data.docking
  corridorState(frame, cfg, st.ship.pos, st.ship.vel, st.fwd, st.up, r)
  if (stepDockHold(r, SIM_DT, cfg)) completeDock(st, sim, p, target)
}

/** The clamps: zero control, thrust and velocity where the ship is; clearance used; `docked`. */
function completeDock(st: DockingState, sim: Sim, p: number, station: number): void {
  stopAutodock(st, false)
  releaseClearance(st.clearance)
  zeroShipControl(p)
  zeroThrust(p)
  st.quat.x = Rot.x[p]
  st.quat.y = Rot.y[p]
  st.quat.z = Rot.z[p]
  st.quat.w = Rot.w[p]
  const x = Pos64.x[p]
  const y = Pos64.y[p]
  const z = Pos64.z[p]
  setShipPose(sim, p, x, y, z, st.quat, 0, 0, 0)
  st.docked = true
  st.dockedStation = station
  markDocked(st.readout)
  syncPhase(st)
  emitSpaceEvent(st.ctx.events, SEV.docked, p, station, 0, 0, x, y, z, 0, 0)
}

/** The moor flight's control step (M10 P1e2): flight input cancels it; else `stepMoorFlight` writes `Control`. */
function mooringControl(st: DockingState, sim: Sim, p: number): void {
  const flow = st.moorFlow
  if (st.ctx.input.flightActive) {
    cancelAutodock(st)
    return
  }
  const s = st.stations.list[flow.station]
  const frame = s.bay
  if (!frame) {
    cancelAutodock(st)
    return
  }
  Ship.faOn[p] = 1
  readShip(st, p)
  stepMoorFlight(flow, p, st.ship.pos, st.ship.rot, s.pos, s.radius, frame, sim.defs[Ship.defIndex[p]], st.moor, st.ctx.data.docking.autopilot)
}

/** The moor hold is met (M10 P1e2): the pilot's flight assist back, the same dock completion, then `moored`. */
function completeMoor(st: DockingState, sim: Sim, p: number): void {
  const flow = st.moorFlow
  const station = flow.station
  const slot = flow.slot
  Ship.faOn[p] = flow.faBefore
  setMoored(flow, station, slot)
  completeDock(st, sim, p, station)
  emitSpaceEvent(st.ctx.events, SEV.moored, p, station, slot, 0, Pos64.x[p], Pos64.y[p], Pos64.z[p], 0, 0)
}

/** `applyStationImpacts`' scratch (D-014): the tick's thrust and the impact floats. */
const impactThrust = new ThrustSample()
const impactFloats = new ImpactFloats()

/**
 * Station impacts for the player (module doc): within `impact.proximity` of a ready station's bounds, the velocity
 * change this tick's thrust does not explain is hull damage. Reads `st.ship` (the post-sync state).
 */
export function applyStationImpacts(st: DockingState, sim: Sim, p: number): void {
  if (!st.preValid) return
  const cfg = st.ctx.data.docking
  const list = st.stations.list
  const s = st.ship.pos
  let station = -1
  for (let i = 0; i < list.length && station < 0; i++) {
    const t = list[i]
    if (!t.ready) continue
    const ox = s.x - t.pos.x
    const oy = s.y - t.pos.y
    const oz = s.z - t.pos.z
    const d = Math.sqrt(ox * ox + oy * oy + oz * oz)
    if (d <= t.radius + cfg.impact.proximity) station = i
  }
  if (station < 0) return
  const def = sim.defs[Ship.defIndex[p]]
  // Objects only across the un-inlined calls: the Thrust floats, the speed and the damage boxed per tick (DA-1
  // follow-up, D-TBD).
  const t = impactThrust
  t.surge = Thrust.surge[p]
  t.sway = Thrust.sway[p]
  t.heave = Thrust.heave[p]
  t.boosting = Ship.boosting[p] === 1
  thrustAccelerationFrom(def, st.preRot, t, st.accel)
  const e = st.expected
  e.x = st.preVel.x + st.accel.x * SIM_DT
  e.y = st.preVel.y + st.accel.y * SIM_DT
  e.z = st.preVel.z + st.accel.z * SIM_DT
  const v = st.ship.vel
  const k = impactFloats
  k.maxAccel = 0
  k.dt = SIM_DT
  impactSpeedTo(e, v, k)
  impactDamageTo(cfg, k)
  const speed = k.speed
  const damage = k.damage
  if (damage <= 0) return
  // The push direction (away from the station surface) and the hit point on the ship's hull on the other side.
  let nx = v.x - e.x
  let ny = v.y - e.y
  let nz = v.z - e.z
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz)
  nx /= nl
  ny /= nl
  nz /= nl
  const hull = Hull.value[p] - damage
  Hull.value[p] = hull > 0 ? hull : 0
  const rad = def.colliderRadius
  emitEvent(sim.events, EV.hitHull, s.x - nx * rad, s.y - ny * rad, s.z - nz * rad, nx, ny, nz, p, -1, 0, damage)
  emitSpaceEvent(st.ctx.events, SEV.impact, p, station, 0, 0, s.x, s.y, s.z, damage, speed)
}

/** End stage: a dead player loses clearance and auto-dock; the clearance window expires. */
export function dockingEnd(st: DockingState, sim: Sim): void {
  if (st.carrier !== null) cradleEnd(st, sim)
  if (livePlayer(sim) < 0 && !st.docked) {
    stopAutodock(st, false)
    stopMoor(st, false)
    if (st.clearance.status === CLEARANCE_STATUS.granted) releaseClearance(st.clearance)
  }
  if (stepClearance(st.clearance, st.ctx.mirror.gameTime)) {
    emitSpaceEvent(st.ctx.events, SEV.clearanceExpired, sim.playerEid, st.clearance.station, 0, 0, 0, 0, 0, 0, 0)
    stopAutodock(st, true)
  }
  syncPhase(st)
}

// ---------------------------------------------------------------------------------------------
// The cradle (M10 P5a9b2, plan 2026-10-17-m10-p5a P5a9b, Q9): an S docks into its own holding L's `DOCK_CRADLE`
// through `space/cradleFlow.ts`, rides there detached (pinned to the pad each tick by `sim.ts pinCarriedPose`) and
// launches out of the cradle mouth. Nothing here runs unless an own L is registered (`setCradleCarrier`; today only
// the test build's `__debug.m2.spawnOwnL`, P5a10a's parked L later), so today's game is unchanged.
// Per-tick paths are allocation-free (D-014); `completeCradle` and `launchCradle` detach and attach a Rapier body
// (rare events that allocate inside Rapier's binding, as `detachShip`/`attachShip` say).
// ---------------------------------------------------------------------------------------------

/**
 * Reads the registered carrier's pose and motion into `st.carrierView`. False when there is none, it is dead or
 * gone, its slot now holds another def (a recycled eid, review C3), or the player flies it. Allocation-free.
 */
function readCarrier(st: DockingState, sim: Sim): boolean {
  const c = st.carrier
  if (c === null) return false
  const e = c.eid
  if (!carrierAlive(st, sim) || e === sim.playerEid) return false
  const v = st.carrierView
  v.pos.x = Pos64.x[e]
  v.pos.y = Pos64.y[e]
  v.pos.z = Pos64.z[e]
  v.rot.x = Rot.x[e]
  v.rot.y = Rot.y[e]
  v.rot.z = Rot.z[e]
  v.rot.w = Rot.w[e]
  v.vel.x = Vel.x[e]
  v.vel.y = Vel.y[e]
  v.vel.z = Vel.z[e]
  v.angVel.x = AngVel.x[e]
  v.angVel.y = AngVel.y[e]
  v.angVel.z = AngVel.z[e]
  v.radius = c.radius
  return true
}

/** The registered carrier's entity still exists, is alive and still has the def it was registered with (C3). */
function carrierAlive(st: DockingState, sim: Sim): boolean {
  const c = st.carrier
  if (c === null) return false
  const e = c.eid
  return e >= 0 && entityExists(sim.world, e) && Life.alive[e] === 1 && Ship.defIndex[e] === st.carrierDef
}

/** The L's helm while it is ordered to hold (Q9): no stick and flight assist on, so the flight model brakes it to rest. */
function holdCarrier(st: DockingState, e: number): void {
  if (st.carrierFaBefore < 0) st.carrierFaBefore = Ship.faOn[e]
  zeroShipControl(e)
  Ship.faOn[e] = 1
}

/** The hold order is lifted: the L's own flight assist back (review C7), when it is still the live carrier. */
function liftCarrierHold(st: DockingState, sim: Sim): void {
  const was = st.carrierFaBefore
  const c = st.carrier
  st.carrierFaBefore = -1
  st.cradleWaiting = false
  if (was >= 0 && c !== null && carrierAlive(st, sim)) Ship.faOn[c.eid] = was
}

/** Ends the flow and lifts the hold (every exit: cancel, abort, launch, release). Allocation-free. */
function endCradle(st: DockingState, sim: Sim): void {
  cancelCradle(st.cradle)
  liftCarrierHold(st, sim)
}

/** `DockingRuntime.setCradleCarrier`: a carried S or a running approach for the old carrier is released first. */
export function setCradleCarrier(st: DockingState, link: CradleCarrierLink | null): void {
  if (st.carrier !== null && (link === null || link.eid !== st.carrier.eid)) {
    if (st.carried) releaseCarried(st, st.ctx.sim)
    else stopCradle(st)
  }
  const sim = st.ctx.sim
  if (link !== null && (st.carrier === null || link.eid !== st.carrier.eid)) {
    st.carrierDef = link.eid >= 0 && entityExists(sim.world, link.eid) ? Ship.defIndex[link.eid] : -1
    st.carrierFaBefore = -1
  }
  st.carrier = link
  if (link === null) st.carrierDef = -1
  syncPhase(st)
}

/**
 * The carrier died or its slot was reused (C3): the S is released or the approach stopped, and the link dropped, so
 * a recycled eid never becomes the carrier. Allocation-free.
 */
function dropDeadCarrier(st: DockingState, sim: Sim): void {
  if (st.carried) releaseCarried(st, sim)
  else stopCradle(st)
  st.carrier = null
  st.carrierDef = -1
  st.carrierFaBefore = -1
  syncPhase(st)
}

/** `DockingRuntime.cradleKey` (the dock key, before the station pick). Allocation-free. */
export function cradleKey(st: DockingState): boolean {
  const c = st.carrier
  if (c === null) return false
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  if (p < 0) return false
  if (st.carried) return launchCradle(st)
  if (st.cradle.phase === CRADLE_PHASE.approach || st.cradleWaiting) return true
  if (st.docked || st.autodocking || moorFlying(st.moorFlow)) return false
  if (!readCarrier(st, sim)) return false
  readShip(st, p)
  const res = requestCradle(st.cradle, st.ship.pos, sim.defs[Ship.defIndex[p]], st.carrierView, st.cradleTuning)
  // Q9 (review C6): a request orders the hold. A moving L is told to hold and the request waits for it to settle.
  if (res === 'notHolding') st.cradleWaiting = true
  else if (res !== 'ok') return false
  st.cradleFaBefore = Ship.faOn[p]
  Ship.faOn[p] = 1
  holdCarrier(st, c.eid)
  syncPhase(st)
  return true
}

/** Ends a cradle approach or wait (flight input, a lost carrier, an abort): the pilot's flight assist back, the hold lifted. */
function stopCradle(st: DockingState): void {
  if (st.cradle.phase === CRADLE_PHASE.approach || st.cradleWaiting) {
    const p = st.ctx.sim.playerEid
    if (p >= 0) Ship.faOn[p] = st.cradleFaBefore
  }
  endCradle(st, st.ctx.sim)
}

/** While carried no tool fires (review C1): the tools stage ran first, so its intent is cleared here. */
function clearCarriedTools(p: number): void {
  ToolControl.fire[p] = 0
  ToolControl.alt[p] = 0
}

/** Control stage, cradle part. True when it owns the player's `Control` this tick. Allocation-free. */
function cradleControl(st: DockingState, sim: Sim, p: number): boolean {
  const flow = st.cradle
  const c = st.carrier
  if (c === null) return false
  if (!carrierAlive(st, sim)) {
    const owned = st.carried || st.cradleWaiting || flow.phase === CRADLE_PHASE.approach
    dropDeadCarrier(st, sim)
    if (owned) zeroShipControl(p)
    return owned
  }
  const live = readCarrier(st, sim)
  if ((flow.holdOrdered || st.cradleWaiting) && live) holdCarrier(st, c.eid)
  if (st.carried) {
    zeroShipControl(p)
    clearCarriedTools(p)
    return true
  }
  if (st.cradleWaiting) {
    if (st.ctx.input.flightActive || !live) {
      stopCradle(st)
      syncPhase(st)
      return true
    }
    zeroShipControl(p)
    Ship.faOn[p] = 1
    readShip(st, p)
    const res = requestCradle(flow, st.ship.pos, sim.defs[Ship.defIndex[p]], st.carrierView, st.cradleTuning)
    if (res === 'ok') st.cradleWaiting = false
    else if (res !== 'notHolding') stopCradle(st)
    syncPhase(st)
    return true
  }
  if (flow.phase !== CRADLE_PHASE.approach) return false
  if (st.ctx.input.flightActive || !live) {
    stopCradle(st)
    syncPhase(st)
    return true
  }
  Ship.faOn[p] = 1
  readShip(st, p)
  const res = stepCradleFlow(flow, st.carrierView, c.socket, st.ship, sim.defs[Ship.defIndex[p]], st.ctx.data.docking, st.cradleTuning, SIM_DT, st.control)
  const k = st.control
  Control.surge[p] = k.surge
  Control.sway[p] = k.sway
  Control.heave[p] = k.heave
  Control.pitch[p] = k.pitch
  Control.yaw[p] = k.yaw
  Control.roll[p] = k.roll
  Control.boost[p] = 0
  if (res === 'arrived') completeCradle(st, sim, p)
  else if (res === 'aborted' || res === 'failed') {
    Ship.faOn[p] = st.cradleFaBefore
    liftCarrierHold(st, sim)
    syncPhase(st)
  }
  return true
}

/** The autopilot arrived in the cradle: the S leaves Rapier and rides on the pad; `Career.carriedBy` is written. */
function completeCradle(st: DockingState, sim: Sim, p: number): void {
  const c = st.carrier
  if (c === null) return
  Ship.faOn[p] = st.cradleFaBefore
  zeroShipControl(p)
  zeroThrust(p)
  detachShip(sim, p)
  st.carried = true
  st.preValid = false
  pinCarried(st, sim, p)
  syncPhase(st)
  c.onCarried?.(c.fleetId)
}

/**
 * The carried pose (physics stage, after the body sync): the cradle frame rebuilt from the L's pose this tick, the
 * S's hull box centred on the pad facing in, moving with the pad (v + ω × r). A lost carrier releases the S where it
 * is. When something gave the S a Rapier body again (cruise ends with `attachShip`), `pinCarriedPose` refuses; the S
 * is detached again and pinned (review C1). Allocation-free (the re-detach is a rare event inside Rapier).
 */
export function pinCarried(st: DockingState, sim: Sim, p: number): void {
  const c = st.carrier
  if (c === null || !readCarrier(st, sim)) {
    releaseCarried(st, sim)
    return
  }
  const frame = cradleFrame(st.carrierView.pos, st.carrierView.rot, c.socket, st.cradle.frame)
  bayFacing(frame, 1, st.quat)
  // Objects only across these calls (P5a11b: floats passed to an un-inlined call box, D-1052).
  const o = originForHullCentreFrom(frame.pad, st.quat, sim.defs[Ship.defIndex[p]].hullCentre, poseOrigin)
  const v = cradlePadVelocity(st.carrierView, frame.pad, st.padVel)
  if (pinCarriedPoseFrom(sim, p, o, st.quat, v)) return
  detachShip(sim, p)
  pinCarriedPoseFrom(sim, p, o, st.quat, v)
}

/** Puts a carried S back into Rapier where it is (a lost carrier, a new one): not carried, carriedBy cleared. */
function releaseCarried(st: DockingState, sim: Sim): void {
  if (!st.carried) return
  const p = sim.playerEid
  st.carried = false
  endCradle(st, sim)
  if (p >= 0 && Life.alive[p] === 1) attachShip(sim, p)
  syncPhase(st)
  st.carrier?.onCarried?.(undefined)
}

/**
 * `DockingRuntime.launchCradle`: the S leaves the cradle nose out at `cradle.launchSpeedMps` along the cradle's out
 * axis (+Y on the Gannet), plus the pad's own velocity, back in Rapier; the L's hold order is lifted (its own flight
 * assist back) and `Career.carriedBy` cleared. It starts from the pad, where it rode, so there is no jump to the
 * mouth (review C7): its hull box stays centred on the pad, only turned nose out.
 */
export function launchCradle(st: DockingState): boolean {
  if (!st.carried) return false
  const sim = st.ctx.sim
  const p = livePlayer(sim)
  const c = st.carrier
  if (p < 0 || c === null || !readCarrier(st, sim)) {
    releaseCarried(st, sim)
    return p >= 0
  }
  const frame = cradleFrame(st.carrierView.pos, st.carrierView.rot, c.socket, st.cradle.frame)
  bayFacing(frame, -1, st.quat)
  const m = originForHullCentre(frame.pad.x, frame.pad.y, frame.pad.z, st.quat, sim.defs[Ship.defIndex[p]].hullCentre, poseOrigin)
  const v = cradlePadVelocity(st.carrierView, frame.pad, st.padVel)
  const speed = st.cradleCfg.launchSpeedMps
  setShipPose(sim, p, m.x, m.y, m.z, st.quat, v.x + frame.out.x * speed, v.y + frame.out.y * speed, v.z + frame.out.z * speed)
  attachShip(sim, p)
  zeroThrust(p)
  st.carried = false
  st.preValid = false
  endCradle(st, sim)
  clearGuides(st.readout)
  st.readout.speed = speed
  syncPhase(st)
  c.onCarried?.(undefined)
  return true
}

/** End stage, cradle part: a dead player ends the approach or the carry. Allocation-free. */
function cradleEnd(st: DockingState, sim: Sim): void {
  if (livePlayer(sim) >= 0) return
  if (st.carried) {
    st.carried = false
    endCradle(st, sim)
    st.carrier?.onCarried?.(undefined)
  } else if (st.cradle.phase !== CRADLE_PHASE.none || st.cradleWaiting) endCradle(st, sim)
}

/** `DockingRuntime.cradleStatus`: the pooled status (copy it to keep it). */
export function cradleStatusOf(st: DockingState): CradleStatus {
  const s = st.cradleStatus
  const ph = st.cradle.phase
  s.phase = ph === CRADLE_PHASE.approach ? 'approach' : ph === CRADLE_PHASE.cradled ? 'cradled' : 'none'
  s.holdOrdered = st.cradle.holdOrdered
  s.carried = st.carried
  s.carrier = st.carrier === null ? -1 : st.carrier.eid
  return s
}
